import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../../hoc/withAuthentication';
import withLayout from '../../../hoc/withLayout';

import { useAlertStore } from '../../../store/providers/Alert';
import { useMarketplaceCategoryStore } from '../../../store/providers/MarketplaceCategory';
import Permission from '../../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../../store/actions/breadcrumb';

import Spinner from '../../../components/Spinner/Spinner';

import * as constants from '../../../constants';
import { columns, sorting } from './MarketplaceCategoriesSchema';

const SideMenu = React.lazy(() => import('../../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../../components/PageHeader/PageHeader'));
const BaseList = React.lazy(() => import('../../../components/Lists/BaseList/BaseList'));
const MarketplaceCategoryModal = React.lazy(() => import('../../../components/Modals/MarketplaceCategoryModal/MarketplaceCategoryModal'));

function MarketplaceCategories() {
    // Redux state
    const dispatch = useDispatch();

    // Store state
    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useMarketplaceCategoryStore();

    // Local state
    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    const pagePermission = constants.PERMISSION_MARKETPLACE_CATEGORIES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const [listData, setListData] = useState([]);
    const [selectedItem, setSelectedItem] = useState();

    const selectedHandler = (marketplaceCategory) => {
        setIsEditing(true);
        setSelectedItem(marketplaceCategory);
    };

    const addedHandler = () => {
        setIsEditing(true);
        setSelectedItem(null);
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const deletedHandler = (marketplaceCategory) => {
        deleteRecord({ marketplaceCategoryId: marketplaceCategory.id });
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list')));
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'marketplace', parent: 'Marketplace', text: 'Marketplace Categories', path: window.location.pathname }));
        listRecords();
        setIsMounted(true);
    }, [hasPermission, isMounted, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="marketplace" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <MarketplaceCategoryModal
                                show={isEditing}
                                canEdit={canEdit}
                                dataItem={selectedItem}
                                title={(!_.isEmpty(data) && !_.isEmpty(data.body)) ? 'Edit Marketplace Category' : 'Add Marketplace Category'}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Marketplace Categories" />
                            <Panel>
                                <Panel.Body>
                                    <div className="row" style={{ paddingTop: '20px' }}>
                                        <div className="col-md-12">
                                            <BaseList
                                                compType="Marketplace Category"
                                                listData={listData}
                                                isBusy={isBusy}
                                                sorting={sorting}
                                                columns={columns}
                                                hideCopy={true}
                                                hidePrint={true}
                                                activeValue="Active"
                                                activeField="statusName"
                                                permissionEdit={canEdit}
                                                added={() => addedHandler()}
                                                selected={(marketplaceCategory) => selectedHandler(marketplaceCategory)}
                                                deleted={(marketplaceCategory) => deletedHandler(marketplaceCategory)}
                                                refreshed={() => refreshedHandler()} />
                                        </div>
                                    </div>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
   );

}

export default withAuthentication(withLayout(MarketplaceCategories));
